import { ActionHandler } from "..";
import { loadSubscriptionStatus } from "src/hooks/subscriptionStatus";
import { clinicalPlanIdPrefix, clinicalPlans } from "@utils/plansMapping";
import {
  clearProductSwitchData,
  ProductSwitchPreviewOrder,
  setPreviewOrder,
  setProductSwitchData,
} from "src/utils/redux/slices/productSwitch";
import getStore from "src/utils/redux/store";
import {
  ProductSwitchOfferQuery,
  queryProductSwitchOffers,
} from "src/api/productSwitchOffers";
import { captureException } from "@utils/error";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { setRecommendationContext } from "src/utils/redux/slices/recommendationContext";
import { getMedSKUByProductName } from "src/utils/plans";
import { SaleItemInfo } from "src/utils/redux/slices/checkoutOrders";
import { createOrderRequest } from "src/utils/api/purchase/ordersCheckoutApi";
import { extractBillingCycles } from "src/hooks/checkout/ordersCheckout";
import { convertGqlSwitchOperationToProto } from "src/utils/orderConversions";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { getActiveUser } from "@noom/hive-auth";

async function createPreviewOrderForOffer(
  offer: ProductSwitchOfferQuery,
  accessCode: string
): Promise<ProductSwitchPreviewOrder> {
  const task = trackTask("ProductSwitchPreviewOrder", {
    productName: offer.productName,
  });
  task.start();
  if (!offer.switchOperation) {
    const err = new Error(`No switchOperation for offer ${offer.productName}`);
    task.failed(err);
    throw err;
  }
  try {
    const switchOperation = convertGqlSwitchOperationToProto(
      offer.switchOperation
    );
    const order = await createOrderRequest(
      { switchOperation },
      accessCode,
      true
    );
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(order);
    task.success();
    return { initialBillingCycle, recurringBillingCycle };
  } catch (error) {
    task.failed(error);
    throw error;
  }
}

export async function loadAndStoreProductSwitchData() {
  const { productSwitch } = getStore().getState();
  if (productSwitch?.loaded) return;

  try {
    const [subscriptionStatus, offers] = await Promise.all([
      loadSubscriptionStatus(),
      queryProductSwitchOffers(),
    ]);

    const planId = subscriptionStatus?.planId;

    let currentProductName: string | null = null;
    if (planId) {
      if (planId.startsWith(clinicalPlanIdPrefix.compoundedMicrodose)) {
        currentProductName = clinicalPlans.compoundedMicrodose;
      } else if (planId.startsWith(clinicalPlanIdPrefix.compounded)) {
        currentProductName = clinicalPlans.compounded;
      } else if (
        planId.startsWith(clinicalPlanIdPrefix.compoundedTirzepatide)
      ) {
        currentProductName = clinicalPlans.compoundedTirzepatide;
      }
    }

    getStore().dispatch(
      setProductSwitchData({
        currentProductName,
        currentPlanPrice: subscriptionStatus?.planPrice,
        currentBillingInterval:
          subscriptionStatus?.billingCycleInterval &&
          subscriptionStatus?.billingCycleIntervalCount
            ? {
                unit: subscriptionStatus.billingCycleInterval,
                count: subscriptionStatus.billingCycleIntervalCount,
              }
            : null,
        previewOrders: {},
        offers,
        loaded: true,
      })
    );

    const eligibleSaleItems: SaleItemInfo[] = offers.reduce<SaleItemInfo[]>(
      (items, offer) => {
        const medSku = getMedSKUByProductName(offer.productName);
        if (medSku && offer.switchOperation) {
          items.push({
            medSku,
            saleItemId: offer.switchOperation.saleItemId,
            trialSaleItemId: offer.switchOperation.trialSaleItemId,
            planName: offer.productName,
          });
        }
        return items;
      },
      []
    );

    getStore().dispatch(setRecommendationContext({ eligibleSaleItems }));
  } catch (error) {
    captureException(error, "load-product-switch-offer-failed");
    getStore().dispatch(clearProductSwitchData());
    getStore().dispatch(setRecommendationContext({ eligibleSaleItems: [] }));
    trackBuyflowEvent("BuyflowActionFailed", {
      errorMessage: `Failed to load product switch data: ${error}`,
    });
  }
}

function isPreviewOrderResolved(entry?: ProductSwitchPreviewOrder): boolean {
  if (!entry) return false;
  return !!entry.recurringBillingCycle || !!entry.error;
}

let previewOrdersLoading = false;

/**
 * Loads preview orders for all product switch offers and stores them in Redux.
 * Each offer resolves independently so prices appear as soon as they're ready.
 * Skips products that already have data or an explicit error. Entries stuck in
 * a loading state (e.g. from a page refresh that interrupted the API call) are
 * retried.
 */
export async function loadPreviewOrders() {
  if (previewOrdersLoading) return;
  previewOrdersLoading = true;
  const store = getStore();
  const { offers, previewOrders = {} } = store.getState().productSwitch;

  const accessCode = getActiveUser()?.accessCode;
  const offersToLoad = offers.filter(
    (offer) => !isPreviewOrderResolved(previewOrders[offer.productName])
  );

  if (offersToLoad.length === 0 || !accessCode) {
    previewOrdersLoading = false;
    return;
  }

  for (const offer of offersToLoad) {
    store.dispatch(
      setPreviewOrder({
        productName: offer.productName,
        previewOrder: { loading: true },
      })
    );
  }

  await Promise.allSettled(
    offersToLoad.map(async (offer) => {
      try {
        const preview = await createPreviewOrderForOffer(offer, accessCode);
        store.dispatch(
          setPreviewOrder({
            productName: offer.productName,
            previewOrder: preview,
          })
        );
      } catch (error) {
        captureException(error);
        store.dispatch(
          setPreviewOrder({
            productName: offer.productName,
            previewOrder: { error: true },
          })
        );
      }
    })
  );
  previewOrdersLoading = false;
}

export const loadProductSwitchData: ActionHandler = async () => {
  await loadAndStoreProductSwitchData();
};
